import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { type PhoneError, PhoneInput } from "@sprout/phone_input.tsx";
import { type FieldValue } from "./field";

interface Props {
  label: string;
  name: string;
  required: boolean;
  countryError: string | null | undefined;
  phoneError: string | null | undefined;
  value: string | null;
  onChange: (value: FieldValue) => void;
  onError: (error: PhoneError | null) => void;
}

const PhoneField = ({
  label,
  name,
  required,
  countryError,
  phoneError,
  value,
  onChange,
  onError,
}: Props) => {
  const { t } = useTranslation("job_post");
  const countryRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  // Sprout's PhoneInput keys an effect on its own useCallbacks, which derive
  // from `onChange` / `onError`. If the parent passes fresh arrows every
  // render, that effect re-fires each render and triggers an infinite
  // setState loop. Hold the latest props in refs and forward through stable
  // callbacks.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const stableOnChange = useCallback((next: string) => {
    onChangeRef.current(next);
  }, []);

  const stableOnError = useCallback((next: PhoneError | null) => {
    onErrorRef.current(next);
  }, []);

  return (
    <div className={required ? "phone-field--required" : undefined}>
      <PhoneInput
        id={name}
        countryLabel={t("application.country")}
        phoneLabel={label}
        required={required}
        countryError={countryError ?? null}
        phoneError={phoneError ?? null}
        maxLength={255}
        initialValue={value || ""}
        onChange={stableOnChange}
        onError={stableOnError}
        countryRef={countryRef}
        phoneRef={phoneRef}
      />
    </div>
  );
};

export default PhoneField;
export type { PhoneError };
